/**
 * Article Interactions Module
 * Handles all user interactions on article pages
 */

class ArticleInteractions {
  constructor() {
    this.articleTitle = document.querySelector('meta[property="og:title"]')?.content || '';
    this.articleExcerpt = document.querySelector('meta[property="og:description"]')?.content || '';
    
    this.initYouTubeFacades();
    this.initActionButtons();
    this.initCommentHandlers();
    this.initChapterToggles();
    this.initEmbeddedContent();
  }

  /**
   * Initialize action buttons (copy, save, download)
   */
  initActionButtons() {
    const copyLinkBtn = document.getElementById('copy-link-btn');
    const saveBtn = document.getElementById('save-article-btn');
    const downloadPdfBtn = document.getElementById('download-pdf-btn');

    // Copy Link
    if (copyLinkBtn) {
      copyLinkBtn.addEventListener('click', () => this.handleCopyLink(copyLinkBtn));
    }

    // Save (bookmark)
    if (saveBtn) {
      saveBtn.addEventListener('click', () => this.handleSave());
    }

    // Download PDF
    if (downloadPdfBtn) {
      const pdfUrl = downloadPdfBtn.dataset.pdfUrl;
      
      if (pdfUrl && pdfUrl.trim() !== '') {
        downloadPdfBtn.addEventListener('click', () => {
          window.location.href = pdfUrl;
        });
      } else if (!downloadPdfBtn.hasAttribute('data-pdf-url')) {
        // Only hide if attribute is completely missing (no @hub)
        downloadPdfBtn.style.display = 'none';
      }
    }
  }

  /**
   * Handle copy link functionality
   */
  handleCopyLink(btn) {
    const url = window.location.href;
    const originalText = btn.innerHTML;

    const showCopied = () => {
      btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Copied!';
      btn.style.color = '#10b981';
      btn.style.borderColor = '#10b981';
      setTimeout(() => {
        btn.innerHTML = originalText;
        btn.style.color = '#000';
        btn.style.borderColor = '#e5e7eb';
      }, 2000);
    };

    const fallbackCopy = () => {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand('copy');
        showCopied();
      } catch (err) {
        alert('Copy failed. URL: ' + url);
      }

      document.body.removeChild(textArea);
    };

    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(showCopied).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  }

  /**
   * Handle save/bookmark functionality
   */
  handleSave() {
    let bookmarked = false;

    // Try older browser APIs first
    if (window.sidebar && window.sidebar.addPanel) {
      // Firefox (older)
      window.sidebar.addPanel(document.title, window.location.href, '');
      bookmarked = true;
    } else if (window.external && ('AddFavorite' in window.external)) {
      // IE/Edge (older)
      try {
        window.external.AddFavorite(window.location.href, document.title);
        bookmarked = true;
      } catch(e) {
        // Silently fail
      }
    }

    if (!bookmarked) {
      // Modern browsers - show helpful modal-style instruction
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const shortcut = isMac ? '⌘+D' : 'Ctrl+D';

      // Create and show a nicer instruction overlay
      const overlay = document.createElement('div');
      overlay.style.cssText = 'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 2rem; border-radius: 0.75rem; box-shadow: 0 20px 50px rgba(0,0,0,0.3); z-index: 9999; text-align: center; max-width: 400px;';
      overlay.innerHTML = `
        <div style="font-size: 3rem; margin-bottom: 1rem;">🔖</div>
        <h3 style="font-size: 1.25rem; font-weight: 600; margin-bottom: 0.5rem; color: #000;">Bookmark This Page</h3>
        <p style="color: #6b7280; margin-bottom: 1.5rem;">Press <kbd style="background: #f3f4f6; padding: 0.375rem 0.75rem; border-radius: 0.5rem; font-family: monospace; color: #000; font-weight: 600; border: 1px solid #e5e7eb;">${shortcut}</kbd> to save this article</p>
        <button onclick="this.parentElement.remove()" style="background: #e91e63; color: white; border: none; padding: 0.625rem 1.5rem; border-radius: 0.5rem; font-weight: 600; cursor: pointer;">Got it</button>
      `;

      const backdrop = document.createElement('div');
      backdrop.style.cssText = 'position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 9998;';
      backdrop.onclick = function() {
        overlay.remove();
        backdrop.remove();
      };

      document.body.appendChild(backdrop);
      document.body.appendChild(overlay);
    }
  }

  /**
   * Initialize comment reply/cancel handlers.
   * Uses a single shared <template id="reply-form-template"> cloned into whichever
   * comment the user clicks Reply on, rather than duplicating a form per comment.
   * Event delegation handles both initial and lazily-loaded comments.
   */
  initCommentHandlers() {
    const template = document.getElementById('reply-form-template');
    if (!template) return;

    let activeSlot = null;
    let activeForm = null;

    const removeActiveForm = () => {
      if (activeForm) {
        activeForm.remove();
        activeForm = null;
        activeSlot = null;
      }
    };

    document.addEventListener('click', (e) => {
      const replyBtn = e.target.closest('.reply-btn');
      if (replyBtn) {
        const commentId = replyBtn.dataset.commentId;
        const comment = document.getElementById(`comment-${commentId}`);
        if (!comment) return;

        const slot = comment.querySelector('.reply-form-slot');
        if (!slot) return;

        // Toggle off if already open on this comment
        if (slot === activeSlot) {
          removeActiveForm();
          return;
        }

        removeActiveForm();

        const clone = template.content.cloneNode(true);
        const parentIdInput = clone.querySelector('input[name*="parent_id"]');
        if (parentIdInput) parentIdInput.value = commentId;

        slot.appendChild(clone);
        activeSlot = slot;
        activeForm = slot.querySelector('form');

        const firstInput = activeForm.querySelector('input[type="text"]');
        if (firstInput) firstInput.focus();
        return;
      }

      if (e.target.closest('.cancel-reply-btn')) {
        removeActiveForm();
      }
    });
  }

  /**
   * Initialize sidebar chapter toggle functionality
   */
  initChapterToggles() {
    const chapterToggles = document.querySelectorAll('.chapter-toggle');

    chapterToggles.forEach(toggle => {
      toggle.addEventListener('click', function() {
        const chapterId = this.dataset.chapterId;
        const articlesDiv = document.querySelector(`.chapter-articles[data-chapter-id="${chapterId}"]`);
        const icon = this.querySelector('.chapter-icon');

        if (articlesDiv) {
          const isExpanded = articlesDiv.classList.contains('is-expanded');

          if (isExpanded) {
            articlesDiv.classList.remove('is-expanded');
            articlesDiv.classList.add('is-collapsed');
            icon.textContent = '▶';
            this.classList.remove('active');
          } else {
            articlesDiv.classList.remove('is-collapsed');
            articlesDiv.classList.add('is-expanded');
            icon.textContent = '▼';
            this.classList.add('active');
          }
        }
      });
    });
  }

  /**
   * Replace YouTube iframes with a click-to-load facade.
   * Eliminates ~800KB of YouTube JS/CSS/fonts until the user clicks play.
   */
  initYouTubeFacades() {
    const body = document.querySelector('.content-article__body');
    if (!body) return;

    body.querySelectorAll('iframe[src*="youtube.com/embed"]').forEach(iframe => {
      const match = iframe.src.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]+)/);
      if (!match) return;

      const videoId = match[1];
      const iframeSrc = iframe.src;

      // Article embeds ship as an absolutely-positioned iframe inside a 16:9
      // padded wrapper (.embedded-youtube-video). In that case the facade must
      // fill the existing wrapper, not add a second padded box — nesting one
      // shifts it down by its margin and clips it (black bar at top).
      const fillsParent = getComputedStyle(iframe).position === 'absolute';

      const wrapper = document.createElement('div');
      wrapper.style.cssText = fillsParent
        ? 'position:absolute;inset:0;overflow:hidden;border-radius:0.5rem;background:#000;cursor:pointer;'
        : 'position:relative;padding-bottom:56.25%;height:0;overflow:hidden;border-radius:0.5rem;background:#000;cursor:pointer;margin:1.5rem 0;';
      wrapper.setAttribute('role', 'button');
      wrapper.setAttribute('tabindex', '0');
      wrapper.setAttribute('aria-label', 'Play video');

      const thumb = document.createElement('img');
      // maxresdefault (1280x720) is sharp and never letterboxed; videos without
      // an HD thumb get a 404 whose body is still a valid 120x90 gray placeholder
      // JPEG, so the browser renders it and onerror never fires — catch that by
      // size too. Fall back to hqdefault (480x360, always exists — its baked-in
      // 4:3 bars are cropped exactly by object-fit:cover).
      const fallback = () => {
        thumb.onerror = thumb.onload = null;
        thumb.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
      };
      thumb.onerror = fallback;
      thumb.onload = () => { if (thumb.naturalWidth <= 120) fallback(); };
      thumb.src = `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
      thumb.alt = '';
      thumb.loading = 'lazy';
      // margin:0 overrides .content-article__body img { margin: 2rem 0 } —
      // margins offset even absolutely-positioned boxes (32px black bar at top)
      thumb.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0.85;margin:0;';

      const btn = document.createElement('div');
      btn.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:64px;height:64px;background:rgba(255,255,255,0.95);border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 20px rgba(0,0,0,0.4);pointer-events:none;';
      btn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24"><polygon points="6,3 20,12 6,21" fill="#e91e63"/></svg>';

      wrapper.appendChild(thumb);
      wrapper.appendChild(btn);

      const load = () => {
        if (wrapper.dataset.loaded) return;
        wrapper.dataset.loaded = '1';
        const src = iframeSrc.includes('?') ? `${iframeSrc}&autoplay=1` : `${iframeSrc}?autoplay=1`;
        const frame = document.createElement('iframe');
        frame.src = src;
        frame.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0;';
        frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        frame.allowFullscreen = true;
        if (fillsParent) {
          wrapper.replaceWith(frame);
        } else {
          // keep the padded wrapper as the aspect box; swap its contents
          wrapper.replaceChildren(frame);
          wrapper.style.cursor = 'default';
          wrapper.removeAttribute('role');
          wrapper.removeAttribute('tabindex');
        }
      };

      wrapper.addEventListener('click', load);
      wrapper.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') load(); });

      iframe.replaceWith(wrapper);
    });
  }

  /**
   * Initialize embedded content (event cards, bingo cards) with lazy loading
   */
  async initEmbeddedContent() {
    // Load event cards if present
    const eventCards = document.querySelectorAll('.embedded-event-card');
    if (eventCards.length > 0) {
      // Dynamic import - only load when needed
      const { initEventCards } = await import('./embedded-content/event-cards.js');
      initEventCards(eventCards);
    }

    // Load bingo cards if present
    const bingoCards = document.querySelectorAll('.embedded-bingo-card');
    if (bingoCards.length > 0) {
      // Dynamic import - only load when needed
      const { initBingoCards } = await import('./embedded-content/bingo-cards.js');
      initBingoCards(bingoCards);
    }

    // Initialize embedded generators
    const generators = document.querySelectorAll('[data-generator-id], [data-generator-slug]');
    if (generators.length > 0) {
      const { initializeGenerators } = await import('./embedded-content/generators.js');
      initializeGenerators();
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  new ArticleInteractions();
});

